import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useRegulatoryFineTrends,
  type FineTrendRow,
  type FineTrendQuarter,
  type RegulatoryFilters,
} from '@/hooks/useRegulatoryQueries'

interface Props {
  filters: Pick<RegulatoryFilters, 'jurisdiction' | 'timeframe' | 'framework'>
}

/**
 * P7 — Fine Trend by Framework (small multiples).
 *
 * Renders one row per qualifying framework (full/partial coverage only —
 * server-enforced). Each row contains three mini line charts: median fine,
 * p95 fine, and count per quarter. Every mini chart is wrapped in an SP3
 * `data-chart-shell="true"` boundary so sub-project 4 can replace the shell.
 *
 * Filter narrowing:
 *   - If Framework filter is 'all': render every framework the server returned.
 *   - If Framework filter names a framework that IS in the full/partial set:
 *     render only that one row.
 *   - If Framework filter names an emerging / pending-ingest framework: the
 *     server filters it out, so `visible` is empty and the widget returns null.
 */

function MiniChart({
  data,
  dataKey,
  title,
  testid,
  formatter,
}: {
  data: FineTrendQuarter[]
  dataKey: 'medianFine' | 'p95Fine' | 'count'
  title: string
  testid: string
  formatter?: (v: number) => string
}) {
  return (
    <div
      data-testid={testid}
      data-chart-shell="true"
      className="rounded-md border p-3"
    >
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {data.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">
          No data
        </div>
      ) : (
        <div className="h-24">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="quarter" hide />
              <YAxis hide allowDecimals={false} />
              <Tooltip
                formatter={(v: number) =>
                  formatter ? formatter(v) : String(v)
                }
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke="var(--cat-1)"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

const currencyFmt = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

function formatFine(v: number): string {
  return `€${currencyFmt.format(v)}`
}

function FrameworkRow({ fw }: { fw: FineTrendRow }) {
  return (
    <section
      data-testid={`p7-row-${fw.slug}`}
      className="rounded-md border p-4"
      aria-label={`Fine trend: ${fw.name}`}
    >
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{fw.name}</h3>
        <span className="text-xs text-muted-foreground">
          {fw.quarters.length} {fw.quarters.length === 1 ? 'quarter' : 'quarters'}
        </span>
      </header>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MiniChart
          data={fw.quarters}
          dataKey="medianFine"
          title="Median fine"
          testid={`p7-mini-median-${fw.slug}`}
          formatter={formatFine}
        />
        <MiniChart
          data={fw.quarters}
          dataKey="p95Fine"
          title="P95 fine"
          testid={`p7-mini-p95-${fw.slug}`}
          formatter={formatFine}
        />
        <MiniChart
          data={fw.quarters}
          dataKey="count"
          title="Count per quarter"
          testid={`p7-mini-count-${fw.slug}`}
        />
      </div>
    </section>
  )
}

export function FineTrendSmallMultiples({ filters }: Props) {
  const { data, isLoading, isError } = useRegulatoryFineTrends({
    jurisdiction: filters.jurisdiction,
    timeframe: filters.timeframe,
  })

  if (isLoading) {
    return (
      <section data-testid="p7-loading" aria-label="Loading fine trends">
        <Skeleton className="h-40 w-full" />
      </section>
    )
  }
  if (isError || !data) {
    return (
      <section role="alert" data-testid="p7-error">
        <p className="text-sm text-destructive">Unable to load fine trends.</p>
      </section>
    )
  }

  // Framework filter narrowing: if a specific framework is selected and it's
  // NOT in the full/partial set returned by the API, hide P7 entirely.
  const visible =
    filters.framework === 'all'
      ? data.frameworks
      : data.frameworks.filter((f) => f.slug === filters.framework)

  if (visible.length === 0) {
    return null
  }

  return (
    <section data-testid="p7-fine-trends" aria-label="Fine trend by framework">
      <header className="mb-3">
        <h2 className="text-lg font-semibold">Fine trend by framework</h2>
        <p className="text-sm text-muted-foreground">
          Median fine, P95 fine, and action count per quarter for frameworks
          with full or partial enforcement coverage.
        </p>
      </header>
      <div className="flex flex-col gap-3">
        {visible.map((fw) => (
          <FrameworkRow key={fw.slug} fw={fw} />
        ))}
      </div>
    </section>
  )
}
